import { NavLink, Outlet } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { useCurrentStaff } from '@/auth/useCurrentStaff';
import { StaffPermissions, type StaffPermission } from '@/auth/staffPermissions';
import { isAuthConfigured } from '@/auth/config';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * The console shell (M1-32): header, primary navigation, and the outlet the
 * later slices render into.
 *
 * The navigation is **built from the permission set `/admin/me` returned**,
 * not from a persona name. That is the acceptance criterion, and it is also
 * what stops this file needing an edit every time the persona matrix changes —
 * a third persona (which M0-33 says is a one-table edit on the server) needs
 * no change here at all.
 */

type NavItem = {
  to: string;
  labelKey: string;
  /** Omitted means "everyone signed in", not "no check". */
  permission?: StaffPermission;
};

const NAV: readonly NavItem[] = [
  { to: '/', labelKey: 'nav.overview' },
  { to: '/tenants', labelKey: 'nav.tenants', permission: StaffPermissions.readTenants },
  {
    to: '/diagnostics',
    labelKey: 'nav.diagnostics',
    permission: StaffPermissions.diagnosticsAccount,
  },
  { to: '/audit', labelKey: 'nav.audit', permission: StaffPermissions.readAudit },
];

export function StaffLayout() {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const { data } = useCurrentStaff();

  const held = data?.permissions ?? [];
  const visible = NAV.filter(
    // Unconfigured auth shows everything, matching the guards: a developer
    // without an app registration still needs to reach the screens.
    (item) => !item.permission || !isAuthConfigured || held.includes(item.permission),
  );

  return (
    <div className="min-h-dvh">
      {/* First focusable thing on the page: a keyboard user should not have to
          tab through the whole nav on every screen. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:m-2 focus:rounded focus:bg-[var(--color-background)] focus:px-3 focus:py-2 focus:underline"
      >
        {t('app.skipToContent')}
      </a>

      <header className="border-b">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-6 gap-y-2 px-6 py-3">
          <span className="font-semibold">{t('app.name')}</span>

          <nav aria-label={t('nav.primary')} className="flex flex-wrap gap-4">
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                // `end` only on the index route, or "/" would match every path.
                end={item.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'text-sm underline-offset-4 hover:underline',
                    // Not colour alone (CC-13): the active item is also
                    // underlined and carries aria-current, so it is
                    // distinguishable without colour vision and announced to a
                    // screen reader.
                    isActive
                      ? 'font-medium text-[var(--color-primary)] underline'
                      : 'text-[var(--color-muted-foreground)]',
                  )
                }
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            {data ? (
              <span className="text-sm text-[var(--color-muted-foreground)]">
                {t('staffMenu.label', { persona: data.persona })}
              </span>
            ) : null}
            {isAuthConfigured ? (
              <Button variant="outline" size="sm" onClick={() => void instance.logoutRedirect()}>
                {t('staffMenu.signOut')}
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto max-w-5xl px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
